import { useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { isEqual } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import { type Level } from "@features/overview-v2/types";
import { useVirtualizer } from "@tanstack/react-virtual";

import { BreakdownFilterItem } from "./breakdown-filter-item";
import { type BreakdownOption, type ListItem } from "./types";
import { buildListItems } from "./utils";

export interface BreakdownFilterProps {
  options: BreakdownOption[];
  breakdownKeys: string[];
  breakdownValues: string[][];
  selectionLimit?: number;
  labelsByLevel?: Record<string, Level>;
  onChange(values: string[][]): void;
  className?: string;
}

export const BreakdownFilter = ({
  options,
  breakdownKeys,
  breakdownValues,
  selectionLimit,
  labelsByLevel,
  onChange,
  ...props
}: BreakdownFilterProps) => {
  const scrollElRef = useRef<HTMLDivElement>(null);

  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => setSearch(""), [breakdownKeys, setSearch]);
  useEffect(
    () => setExpandedSections([]),
    [breakdownKeys, setExpandedSections],
  );

  const items = useMemo(() => {
    const items = buildListItems(
      labelsByLevel ?? {},
      options,
      breakdownKeys,
      breakdownValues,
      search,
      0,
    );

    const flattened: ListItem[] = [];

    const flatten = (items: ListItem[]) => {
      items.forEach((item) => {
        flattened.push(item);
        flatten(item.children);
      });
    };

    flatten(items);

    return flattened;
  }, [options, breakdownKeys, breakdownValues, search]);

  const expandedItems = useMemo(
    () =>
      items.filter(
        (e) =>
          e.parentKey === "" ||
          search !== "" ||
          expandedSections.includes(e.parentKey),
      ),
    [items, expandedSections, search],
  );

  const rowVirtualizer = useVirtualizer({
    count: expandedItems.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 32,
    overscan: 5,
  });

  return (
    <div
      {...props}
      className={clsx(
        "border border-v2-secondary rounded-md p-3 text-sm flex flex-col gap-2",
        props.className,
      )}
    >
      <div className="flex justify-between w-[100%]">
        {selectionLimit && (
          <p className="text-[11px] font-bold tracking-wider mb-0 leading-[24px]">
            SELECT UP TO {selectionLimit} BREAKDOWN COMBINATIONS
          </p>
        )}
        {!selectionLimit && breakdownValues.length > 0 && (
          <p className="text-[11px] font-bold tracking-wider mb-0 leading-[24px]">
            REMOVE SELECTION
          </p>
        )}
        {breakdownValues.length > 0 && (
          <GenericTooltip noInfoCircle content="Remove selections">
            <button
              className={clsx(
                "h-[24px] w-[24px] flex-center bg-cosmic-3 text-primary rounded-lg",
                "hover:bg-primary hover:text-white",
              )}
              onClick={() => onChange([])}
            >
              <i className="fa-regular fa-times-circle" />
            </button>
          </GenericTooltip>
        )}
      </div>
      <div className="nb-input-container relative">
        <input
          className="nb-input pr-8"
          type="text"
          placeholder="Search breakdowns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <i
          className={clsx(
            "fa-regular fa-sharp fa-magnifying-glass text-[0.9rem] text-primary",
            "w-4 absolute right-[14px] top-[10px]",
          )}
        ></i>
      </div>
      <div ref={scrollElRef} className="max-h-[200px] overflow-y-auto">
        {items.length === 0 ? (
          <span className="text-gray">NO RESULTS FOUND</span>
        ) : (
          <div
            style={{
              position: "relative",
              height: `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((e) => {
              const item = expandedItems[e.index];
              return (
                <BreakdownFilterItem
                  key={e.key}
                  style={{ transform: `translateY(${e.start}px)` }}
                  item={item}
                  isExpanded={
                    expandedSections.includes(item.key) || search !== ""
                  }
                  onToggle={(value) => {
                    if (search !== "") {
                      return;
                    }

                    if (expandedSections.includes(value)) {
                      setExpandedSections((existing) =>
                        existing.filter((e) => e !== value),
                      );
                    } else {
                      setExpandedSections((existing) => [...existing, value]);
                    }
                  }}
                  checkboxDisabled={
                    selectionLimit
                      ? breakdownValues.length >= selectionLimit
                      : false
                  }
                  onChange={(values) => {
                    const isIncluded = breakdownValues.some((e) =>
                      isEqual(e, values),
                    );

                    if (isIncluded) {
                      onChange(
                        breakdownValues.filter((e) => !isEqual(e, values)),
                      );
                    } else {
                      onChange([...breakdownValues, values]);
                    }
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
